"use client";
import React, { useEffect, useRef, useState } from "react";
import { salesContacts } from "@/data/salesContacts";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "@/app/home/components/HomeLanguageContext";

type SubMenuItem = {
  label: string;
  href: string;
};

type NavItem = {
  label: string;
  href?: string;
  submenu?: SubMenuItem[];
  highlight?: boolean;
};

const navItems: NavItem[] = [
  { label: "Home", href: "#home" },
  { label: "Apa itu Kolabo?", href: "#about" },
  {
    label: "Video Tutorial",
    submenu: [
      {
        label: "Tutorial Dashboard HRM",
        href: "https://youtube.com/playlist?list=PLVzciVmUhmKeC-FO5zVRwCQXKNS2uJ_aD&si=H-WvsxwiyIK2Ft5z",
      },
      {
        label: "Tutorial Aplikasi User/Staff",
        href: "https://www.youtube.com/playlist?list=PLVzciVmUhmKeFH7zL2nvUplecTEJ7-iRl",
      },
      {
        label: "Tutorial Accounting",
        href: "https://youtube.com/playlist?list=PLVzciVmUhmKcqeiJ65MegrFlUIPEpOHNH&si=v7hlAbp6TEqR8tZ5",
      },
      {
        label: "Tutorial CRM",
        href: "https://www.youtube.com/playlist?list=PLVzciVmUhmKdHOgfNrjyMWNWc2Gm0NahN",
      },
    ],
  },
  { label: "Layanan dan Fitur", href: "#fitur" },
  { label: "Solusi", href: "#features" },
  { label: "Harga", href: "#pricing", highlight: true },
  {
    label: "Tools Gratis",
    submenu: [
      { label: "BikinInvoice.id", href: "https://bikininvoice.id" },
      { label: "HitungPPH21.com", href: "https://hitungpph21.com" },
      { label: "Kolabo Meet", href: "https://meet.kolabo.id" },
    ],
  },
];

export default function Header() {
  const { lang, setLang } = useHomeLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [hideMobileChatCta, setHideMobileChatCta] = useState(false);
  const contactWidgetRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const mobileChatRafRef = useRef<number | null>(null);
  const lastScrolledRef = useRef(false);
  const lastProgressRef = useRef(0);

  const navItemsByLang: NavItem[] =
    lang === "id"
      ? navItems
      : [
          { label: "Home", href: "#home" },
          { label: "What is Kolabo?", href: "#about" },
          {
            label: "Video Tutorials",
            submenu: [
              {
                label: "HRM Dashboard Tutorial",
                href: "https://youtube.com/playlist?list=PLVzciVmUhmKeC-FO5zVRwCQXKNS2uJ_aD&si=H-WvsxwiyIK2Ft5z",
              },
              {
                label: "User/Staff App Tutorial",
                href: "https://www.youtube.com/playlist?list=PLVzciVmUhmKeFH7zL2nvUplecTEJ7-iRl",
              },
              {
                label: "Accounting Tutorial",
                href: "https://youtube.com/playlist?list=PLVzciVmUhmKcqeiJ65MegrFlUIPEpOHNH&si=v7hlAbp6TEqR8tZ5",
              },
              {
                label: "CRM Tutorial",
                href: "https://www.youtube.com/playlist?list=PLVzciVmUhmKdHOgfNrjyMWNWc2Gm0NahN",
              },
            ],
          },
          { label: "Services & Features", href: "#fitur" },
          { label: "Solutions", href: "#features" },
          { label: "Pricing", href: "#pricing", highlight: true },
          {
            label: "Free Tools",
            submenu: [
              { label: "BikinInvoice.id", href: "https://bikininvoice.id" },
              { label: "HitungPPH21.com", href: "https://hitungpph21.com" },
              { label: "Kolabo Meet", href: "https://meet.kolabo.id" },
            ],
          },
        ];

  const ctaText = lang === "id" ? "Coba Gratis, Tanpa Ribet!" : "Try Free, No Hassle!";
  const menuOpenLabel = lang === "id" ? "Buka menu" : "Open menu";
  const menuCloseLabel = lang === "id" ? "Tutup menu" : "Close menu";
  const contactTitle = lang === "id" ? "Halo..." : "Hi...";
  const contactSubtitle = lang === "id" ? "Tim Kolabo siap membantu Anda" : "Kolabo team is ready to help you";
  const closeSalesLabel = lang === "id" ? "Tutup pilihan sales" : "Close sales options";
  const discussLabel = lang === "id" ? "Diskusikan Kebutuhan Anda" : "Discuss Your Needs";
  const discussLabelShort = lang === "id" ? "Chat" : "Chat";
  const onlineNowText = lang === "id" ? "Online Sekarang" : "Online";
  const langSwitchAria = lang === "id" ? "Ganti bahasa" : "Switch language";

  useEffect(() => {
    const onScroll = () => {
      if (scrollRafRef.current !== null) return;

      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;

        const nextScrolled = window.scrollY > 30;
        if (nextScrolled !== lastScrolledRef.current) {
          lastScrolledRef.current = nextScrolled;
          setScrolled(nextScrolled);
        }

        if (window.innerWidth >= 1024) {
          const totalHeight = document.documentElement?.scrollHeight - window.innerHeight;
          const nextProgress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
          if (Math.abs(nextProgress - lastProgressRef.current) >= 1) {
            lastProgressRef.current = nextProgress;
            setScrollProgress(nextProgress);
          }
        } else if (lastProgressRef.current !== 0) {
          lastProgressRef.current = 0;
          setScrollProgress(0);
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      setContactOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!contactOpen) return;

    const onDocClick = (event: MouseEvent) => {
      if (!contactWidgetRef.current?.contains(event.target as Node)) {
        setContactOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setContactOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEscape);
    };
  }, [contactOpen]);

  useEffect(() => {
    const updateMobileChatVisibility = () => {
      if (mobileChatRafRef.current !== null) return;

      mobileChatRafRef.current = window.requestAnimationFrame(() => {
        mobileChatRafRef.current = null;

        if (window.innerWidth >= 1024) {
          setHideMobileChatCta((prev) => (prev ? false : prev));
          return;
        }

        const viewportHeight = window.innerHeight || 0;
        const interactiveIds = ["daftar", "fitur", "patch-notes"];
        const isAroundInteractiveSection = interactiveIds.some((id) => {
          const section = document.getElementById(id);
          if (!section) return false;
          const rect = section.getBoundingClientRect();
          return rect.top < viewportHeight * 0.82 && rect.bottom > 110;
        });

        setHideMobileChatCta((prev) => (prev !== isAroundInteractiveSection ? isAroundInteractiveSection : prev));

        if (isAroundInteractiveSection) {
          setContactOpen(false);
        }
      });
    };

    updateMobileChatVisibility();
    window.addEventListener("scroll", updateMobileChatVisibility, { passive: true });
    window.addEventListener("resize", updateMobileChatVisibility);
    return () => {
      window.removeEventListener("scroll", updateMobileChatVisibility);
      window.removeEventListener("resize", updateMobileChatVisibility);
      if (mobileChatRafRef.current !== null) {
        window.cancelAnimationFrame(mobileChatRafRef.current);
      }
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);
  const isTop = !scrolled;

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "glass-nav shadow-nav border-b border-primary/10"
            : "bg-transparent"
        }`}
      >
        <div
          className="absolute bottom-0 left-0 h-[2px] bg-gradient-brand transition-all duration-100"
          style={{ width: `${scrollProgress}%` }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link href="#home" className="flex items-center shrink-0 group">
              <AppImage
                src="/assets/optimized/logokolaboblack-62ea270a62.webp"
                alt="Kolabo logo"
                width={128}
                height={38}
                priority
                className="h-8 w-auto transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            <div className="hidden lg:flex items-center gap-0.5">
              {navItemsByLang.map((item) => {
                if (item.submenu?.length) {
                  return (
                    <div key={item.label} className="relative group">
                      <button
                        type="button"
                        className={`px-4 py-2 text-[13px] font-medium tracking-wide transition-all duration-200 flex items-center gap-1.5 rounded-full ${
                          isTop ? "text-fg/80 hover:text-fg hover:bg-black/5" : "text-fg/65 hover:text-fg"
                        }`}
                      >
                        {item.label}
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="opacity-70"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                      <div className="absolute top-full left-0 pt-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-150">
                        <div className="w-[320px] rounded-lg border border-stone-200 bg-white shadow-card p-2">
                          {item.submenu.map((sub) => (
                            <a
                              key={sub.label}
                              href={sub.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-3 py-2 text-[13px] text-fg/75 hover:text-fg hover:bg-stone-50 rounded-md transition-colors"
                            >
                              {sub.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <a
                    key={item.label}
                    href={item.href}
                    className={`px-4 py-2 rounded-full text-[13px] font-medium tracking-wide transition-all duration-200 ${
                      item.highlight
                        ? isTop
                          ? "text-[#d70000] hover:text-[#c30000]"
                          : "text-[#d70000] hover:text-[#c30000]"
                        : isTop
                        ? "text-fg/80 hover:text-fg hover:bg-black/5"
                        : "text-fg/65 hover:text-fg"
                    }`}
                  >
                    {item.label}
                  </a>
                );
              })}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <div className="inline-flex items-center rounded-full border border-stone-200 bg-white/85 backdrop-blur-sm p-1" aria-label={langSwitchAria}>
                <button
                  type="button"
                  onClick={() => setLang("id")}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors ${
                    lang === "id" ? "bg-primary text-white" : "text-fg/70 hover:text-fg"
                  }`}
                >
                  ID
                </button>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-full transition-colors ${
                    lang === "en" ? "bg-primary text-white" : "text-fg/70 hover:text-fg"
                  }`}
                >
                  EN
                </button>
              </div>
              <a
                href="#daftar"
                className="btn-gradient text-white text-[13px] font-bold px-5 py-2.5 rounded-full shadow-brand flex items-center gap-2 tracking-wide"
              >
                <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-white/80 inline-block" />
                {ctaText}
              </a>
            </div>

            <button
              className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-full transition-all text-fg"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? menuCloseLabel : menuOpenLabel}
            >
              <span className={`w-5 h-0.5 rounded-full transition-all duration-300 bg-current ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`w-5 h-0.5 rounded-full transition-all duration-300 bg-current ${menuOpen ? "opacity-0" : ""}`} />
              <span className={`w-5 h-0.5 rounded-full transition-all duration-300 bg-current ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-400 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-bg-dark/96 backdrop-blur-xl" onClick={closeMenu} />
        <div
          className={`absolute inset-y-0 right-0 w-full max-w-sm bg-bg-dark border-l border-white/10 flex flex-col transition-transform duration-400 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <AppImage
              src="/assets/optimized/logokolaboblack-62ea270a62.webp"
              alt="Kolabo logo"
              width={100}
              height={30}
              className="h-7 w-auto brightness-0 invert"
            />
            <button
              onClick={closeMenu}
              className="w-9 h-9 flex items-center justify-center text-white/50 hover:text-white rounded-full hover:bg-white/10 transition-all"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 p-6 overflow-auto">
            <div className="mb-5 inline-flex items-center rounded-full border border-white/20 bg-white/10 p-1">
              <button
                type="button"
                onClick={() => setLang("id")}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${
                  lang === "id" ? "bg-white text-fg" : "text-white/70 hover:text-white"
                }`}
              >
                ID
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${
                  lang === "en" ? "bg-white text-fg" : "text-white/70 hover:text-white"
                }`}
              >
                EN
              </button>
            </div>
            <div className="space-y-1.5">
              {navItemsByLang.map((item) => (
                <div key={item.label} className="rounded-xl border border-white/10 bg-white/5">
                  {item.href ? (
                    <a
                      href={item.href}
                      onClick={closeMenu}
                      className={`block px-4 py-3 text-sm font-medium transition-colors ${
                        item.highlight ? "text-white" : "text-white/70 hover:text-white"
                      }`}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <div className="px-4 py-3 text-sm font-semibold text-white">{item.label}</div>
                  )}

                  {!!item.submenu?.length && (
                    <div className="pb-2 px-2">
                      {item.submenu.map((sub) => (
                        <a
                          key={sub.label}
                          href={sub.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMenu}
                          className="block px-3 py-2 text-[13px] text-white/70 hover:text-white hover:bg-white/10 rounded-md"
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </nav>

          <div className="p-6 border-t border-white/10">
            <a
              href="#daftar"
              onClick={closeMenu}
              className="btn-gradient w-full text-white text-sm font-semibold py-3.5 rounded-full text-center flex items-center justify-center gap-2 tracking-wide"
            >
              <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-white/80 inline-block" />
              {ctaText}
            </a>
          </div>
        </div>
      </div>

      <div
        ref={contactWidgetRef}
        className={`fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-3 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end gap-3 pointer-events-none transition-all duration-300 ${
          menuOpen
            ? "max-lg:opacity-0 max-lg:translate-y-3"
            : hideMobileChatCta
            ? "max-lg:opacity-0 max-lg:translate-y-3"
            : "opacity-100 translate-y-0"
        }`}
      >
        <div
          className={`w-[340px] max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)] rounded-2xl border border-primary/20 bg-white/90 backdrop-blur-xl shadow-brand-lg overflow-hidden transition-all duration-300 ${
            contactOpen
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 translate-y-2 pointer-events-none"
          }`}
        >
          <div className="px-4 py-3.5 bg-gradient-brand-135">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-heading font-bold tracking-tight text-white">{contactTitle}</p>
                <p className="text-xs text-white/85">{contactSubtitle}</p>
              </div>
              <button
                type="button"
                onClick={() => setContactOpen(false)}
                className="w-7 h-7 rounded-full border border-white/35 text-white hover:bg-white/15 transition-colors"
                aria-label={closeSalesLabel}
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-2.5 space-y-2.5">
            {salesContacts.map((sales) => (
              <a
                key={sales.phone}
                href={`https://wa.me/${sales.phone.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setContactOpen(false)}
                className="flex items-center gap-3 rounded-xl border border-primary/15 bg-gradient-to-r from-white to-brand-50/40 p-2.5 hover:border-primary/30 hover:shadow-brand transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full overflow-hidden border border-primary/20 shrink-0">
                  <AppImage
                    src={sales.avatar}
                    alt={sales.name}
                    width={44}
                    height={44}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-heading font-semibold text-fg truncate">{sales.name}</p>
                  <p className="text-xs text-fg-muted flex items-center gap-1.5">
                    {sales.role}
                    <span className="text-secondary font-semibold">({lang === "id" ? sales.status : onlineNowText})</span>
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setContactOpen((prev) => !prev)}
          className={`h-11 sm:h-12 px-4 sm:px-5 rounded-full bg-gradient-brand-135 text-white font-heading font-bold text-[13px] sm:text-sm shadow-brand hover:shadow-brand-lg transition-all duration-300 flex items-center gap-2 max-w-[calc(100vw-1.25rem)] ${
            menuOpen ? "max-lg:pointer-events-none" : "pointer-events-auto"
          }`}
          aria-label={discussLabel}
          aria-expanded={contactOpen}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span className="sm:hidden">{discussLabelShort}</span>
          <span className="hidden sm:inline">{discussLabel}</span>
        </button>
      </div>
    </>
  );
}
